// ═══════════════════════════════════════════════════════════════
// V2 api-switch.ts — Compatibility shim for V1 import paths
//
// V1 pages import from '../../lib/api-switch'. In V2, this file
// re-exports the V2 equivalents with the same function signatures
// so V1 pages compile without modification.
// ═══════════════════════════════════════════════════════════════

export { TABLES } from '../api/tables';
export { FIELDS } from '../api/fields';

import { fetchRecords, createRecord as v2Create, updateRecord as v2Update, deleteRecord as v2Delete } from '../api/client';

// In dev, call the worker directly. In production (ogemc.pages.dev), route
// through the Pages Function proxy at /api/worker/* which sets the correct
// Origin header and avoids CORS failures.
const _isDev = import.meta.env.DEV;
export const WORKER_URL = _isDev
  ? (import.meta.env.VITE_WORKER_BASE || 'https://ogemc-worker.ogemc.workers.dev')
  : '/api/worker';
export const ORIGIN = 'https://apps.opengov.com';

// ── fetchTable → V2 fetchRecords (returns flat array) ───────────
export async function fetchTable<T = Record<string, unknown>>(
  tableKey: string,
  options?: { limit?: number; token?: string }
): Promise<T[]> {
  const limit = options?.limit ?? 1000;
  const result = await fetchRecords<T>(tableKey, { limit });
  return result.records ?? (result as unknown as { data: T[] }).data ?? [];
}

// ── createRecord — V1 takes Array<[key, value]>, V2 takes plain object ──
export async function createRecord(
  tableKey: string,
  fields: Array<[string, unknown]>,
  _token?: string
): Promise<number> {
  const data: Record<string, unknown> = {};
  for (const [k, v] of fields) data[k] = v;
  const result = await v2Create<Record<string, unknown>>(tableKey, data);
  return (result.record_id ?? result.id ?? result.Rid ?? 0) as number;
}

// ── updateRecord — V1 takes Array<[key, value]>, V2 takes plain object ──
export async function updateRecord(
  tableKey: string,
  rid: number,
  fields: Array<[string, unknown]>,
  _token?: string
): Promise<void> {
  const data: Record<string, unknown> = {};
  for (const [k, v] of fields) data[k] = v;
  await v2Update(tableKey, rid, data);
}

// ── deleteRecord ─────────────────────────────────────────────────
export async function deleteRecord(
  tableKey: string,
  rid: number,
  _token?: string
): Promise<void> {
  await v2Delete(tableKey, rid);
}

// ── callClaude — proxied through worker ──────────────────────────
export async function callClaude(
  prompt: string,
  systemPrompt?: string,
  stream?: boolean,
): Promise<string> {
  const resp = await fetch(`${WORKER_URL}/api/claude`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: 1024,
      stream: stream ?? false,
      messages: [{ role: 'user', content: prompt }],
      system: systemPrompt,
    }),
  });
  if (!resp.ok) throw new Error(`callClaude: ${resp.status}`);
  const data = await resp.json();
  return data?.content?.[0]?.text ?? data?.completion ?? '';
}

// ── User Management (via worker proxy) ───────────────────────────
export interface IgnatiusUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  role?: string;
}

export async function fetchIgnatiusUsers(): Promise<IgnatiusUser[]> {
  const resp = await fetch(`${WORKER_URL}/api/users`, {
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
  });
  if (!resp.ok) throw new Error(`fetchIgnatiusUsers: ${resp.status}`);
  return resp.json();
}

export async function validateEmail(email: string): Promise<unknown> {
  const resp = await fetch(`${WORKER_URL}/api/users/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) throw new Error(`validateEmail: ${resp.status}`);
  return resp.json();
}

export async function createIgnatiusUser(data: {
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
}): Promise<{ success: boolean; userId?: string }> {
  const resp = await fetch(`${WORKER_URL}/api/users/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error(`createIgnatiusUser: ${resp.status}`);
  return resp.json();
}

export async function resetUserPassword(
  email: string,
  newPassword: string,
  _adminToken: string,
): Promise<{ success: boolean }> {
  const resp = await fetch(`${WORKER_URL}/api/users/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify({ email, newPassword }),
  });
  if (!resp.ok) throw new Error(`resetUserPassword: ${resp.status}`);
  return resp.json();
}

// ── loginUser ────────────────────────────────────────────────────
import { login } from '../api/client';
export { login as loginUser };

// ── No-op record event listener (workflow engine handles this differently in V2) ──
type RecordEventListener = (
  tableKey: string,
  event: 'create' | 'update' | 'delete',
  rid: number,
  data: Record<string, unknown>,
  previousData?: Record<string, unknown>,
) => void;

let _recordEventListener: RecordEventListener | null = null;
export function registerRecordEventListener(fn: RecordEventListener) {
  _recordEventListener = fn;
}
